import { useEffect, useRef, useState } from 'preact/hooks'
import type { ConsoleLine } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import { text } from '#contract'
import styles from './console-overlay.module.css'

const HISTORY = 50

function commonPrefix(values: readonly string[]): string {
  const first = values[0]
  if (first === undefined) return ''
  let end = first.length
  for (const value of values) {
    while (end > 0 && !value.startsWith(first.slice(0, end))) end -= 1
  }
  return first.slice(0, end)
}

function completeLine(current: string, matches: readonly string[]): string {
  const filled = matches.length === 1 ? (matches[0] ?? '') : commonPrefix(matches)
  if (filled.length === 0) return current
  const suffix = matches.length === 1 ? ' ' : ''
  if (current.endsWith(' ') || current.endsWith('\t')) return `${current}${filled}${suffix}`
  const cut = Math.max(current.lastIndexOf(' '), current.lastIndexOf('\t'))
  const head = cut === -1 ? '' : current.slice(0, cut + 1)
  return `${head}${filled}${suffix}`
}

/** Bottom-docked command line. Shown only while the console is open. */
export function ConsoleOverlay() {
  const t = useT()
  const client = useClient()
  const open = useView((view) => view.dev.consoleOpen)
  const lines = useView((view) => view.dev.lines)
  const [value, setValue] = useState('')
  const historyRef = useRef<string[]>([])
  const cursorRef = useRef(0)
  const logRef = useRef<HTMLPreElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dockRef = useRef<HTMLElement>(null)
  const stickRef = useRef(true)

  useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
    stickRef.current = true
  }, [open])

  useEffect(() => {
    const log = logRef.current
    if (stickRef.current && log) log.scrollTop = log.scrollHeight
  }, [lines])

  useEffect(() => {
    if (!open || !client) return
    const viewport = window.visualViewport
    const dock = dockRef.current
    if (!viewport || !dock) return
    const sync = (): void => {
      const covered = window.innerHeight - viewport.height - viewport.offsetTop
      dock.style.bottom = `${String(Math.max(0, covered))}px`
    }
    sync()
    viewport.addEventListener('resize', sync)
    viewport.addEventListener('scroll', sync)
    return () => {
      viewport.removeEventListener('resize', sync)
      viewport.removeEventListener('scroll', sync)
    }
  }, [open, client])

  useEffect(() => {
    if (!open || !client) return
    const onKey = (event: KeyboardEvent): void => {
      if (!event.isTrusted) return
      if (event.code !== 'Backquote' && event.code !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      client.console.setOpen(false)
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
    }
  }, [open, client])

  if (!open || !client) return null

  const remember = (line: string): void => {
    const trimmed = line.trim()
    if (trimmed.length === 0) return
    const next = historyRef.current.filter((item) => item !== trimmed)
    next.push(trimmed)
    if (next.length > HISTORY) next.shift()
    historyRef.current = next
    cursorRef.current = next.length
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      const line = value
      setValue('')
      remember(line)
      client.console.execute(line)
      return
    }
    if (event.key === 'Tab') {
      event.preventDefault()
      const matches = client.console.complete(value)
      if (matches.length > 0) setValue(completeLine(value, matches))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (cursorRef.current === 0) return
      cursorRef.current -= 1
      setValue(historyRef.current[cursorRef.current] ?? '')
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (cursorRef.current >= historyRef.current.length) return
      cursorRef.current += 1
      const next = historyRef.current[cursorRef.current]
      setValue(next ?? '')
    }
  }

  return (
    <section class={styles.dock} ref={dockRef} aria-label={t(text('dev.console.open'))}>
      <pre
        class={styles.log}
        ref={logRef}
        onScroll={() => {
          const log = logRef.current
          if (!log) return
          stickRef.current = log.scrollHeight - log.scrollTop - log.clientHeight < 8
        }}
      >
        {lines.map((line) => (
          <Line key={line.id} line={line} />
        ))}
      </pre>
      <input
        ref={inputRef}
        class={styles.input}
        value={value}
        spellcheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        aria-label={t(text('dev.console.input'))}
        onInput={(event) => {
          cursorRef.current = historyRef.current.length
          setValue(event.currentTarget.value)
        }}
        onKeyDown={onKeyDown}
      />
    </section>
  )
}

function Line({ line }: { readonly line: ConsoleLine }) {
  return (
    <div class={styles.line} data-level={line.level}>
      {line.text.length > 0 ? line.text : ' '}
    </div>
  )
}
